/**
 * Settings → Billing & usage — `screens.md` §9's Active state, its never-404 card, the plan
 * picker for a first purchase (U58) and the one for a switch between paid tiers (U58a). A
 * running trial's card takes the plan card's place (U61).
 *
 * Every list price is read from `PLANS` in `packages/shared`, and every pro-rated figure from
 * Stripe's invoice preview. The pickers render `CHECKOUT_TIERS`, never `TIERS` (`tiers.ts`).
 * The `subscriptions` row picks the picker, never `plan` — Drift Log §U58a. Meters are U59's.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";

import {
  CHECKOUT_TIERS,
  ENDED_SUBSCRIPTION_STATUSES,
  ENTITLING_SUBSCRIPTION_STATUSES,
  PLANS,
  graceEndsAt,
  type BillingInterval,
  type BillingState,
  type CheckoutTier,
  type ManageIntent,
  type SubscriptionView,
  type SwitchPreview,
  type Tier,
} from "@packagepage/shared";
import {
  BillingSurface,
  Button,
  Surface,
  SurfaceBody,
  SurfaceHeader,
} from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import { UsageCard } from "./UsageMeters.js";
import {
  BILLING_STATE_TONES,
  dayName,
  degradedByPayment,
  fairUseSentence,
  paymentFailureSentence,
  type BillingStanding,
} from "./billing-states.js";
import {
  daysLeftOf,
  phaseOf,
  trialRunning,
  trialUsageRows,
  type TrialStanding,
} from "./trial-states.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/pill.css";
import "@packagepage/ui/button.css";
import "./settings.css";

/** `$1,990` — the same spelling as `apps/site`'s `formatUsd`. */
function usd(amount: number): string {
  return `$${amount.toLocaleString("en-US")}`;
}

function tierName(tier: Tier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/** `26 November` — the period end Stripe reported, in UTC so every viewer reads one day. */
export function chargeDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/** The plan card's top, for one plan. Exported for the static suite. */
export function PlanSummary({
  plan,
  billingStatus,
  nextCharge = null,
}: {
  readonly plan: Tier;
  readonly billingStatus: BillingState;
  /** The current period's end, when the caller may read the subscription and it is active. */
  readonly nextCharge?: string | null;
}): ReactNode {
  const { monthlyUsd, yearlyUsd, fromUsd } = PLANS[plan];
  return (
    <Surface data-plan={plan}>
      <div className="pp-billing__plan">
        <h2 className="pp-billing__plan-name">{tierName(plan)}</h2>
        {billingStatus === "active" && (
          <Surface tone="inert" size="pill">
            Active
          </Surface>
        )}
      </div>
      <p className="pp-billing__price">
        {monthlyUsd === null
          ? `From ${usd(fromUsd ?? 0)}, priced per agreement.`
          : `${usd(monthlyUsd)} a month`}
      </p>
      {yearlyUsd !== null && (
        <p className="pp-billing__yearly">
          Yearly is {usd(yearlyUsd)} — two months free.
        </p>
      )}
      {nextCharge !== null && (
        <p className="pp-billing__yearly" data-next-charge>
          Next charge {chargeDate(nextCharge)}.
        </p>
      )}
    </Surface>
  );
}

/** What one tier costs at one interval, as the picker states it before the customer leaves. */
export function checkoutStatement(tier: CheckoutTier, interval: BillingInterval): string {
  const { monthlyUsd, yearlyUsd } = PLANS[tier];
  const amount = interval === "month" ? monthlyUsd : yearlyUsd;
  return (
    `You'll pay ${usd(amount ?? 0)} today, then ${usd(amount ?? 0)} each ` +
    `${interval === "month" ? "month" : "year"}. Payment happens on our payment ` +
    `provider's page — you'll come straight back here.`
  );
}

/**
 * The first-purchase picker. Exported for the static suite; the click's behaviour is the e2e
 * flow's, because a static render fires no event.
 */
export function PlanPicker({
  quiet = false,
  interval,
  onInterval,
  onChoose,
  choosing,
  refusal,
}: {
  readonly interval: BillingInterval;
  readonly onInterval: (interval: BillingInterval) => void;
  readonly onChoose: (tier: CheckoutTier) => void;
  /** The tier whose checkout is being opened, if one is. */
  readonly choosing: CheckoutTier | null;
  readonly refusal: string | null;
  /** Churned: no tier button is primary — the mock's state 5 note. */
  readonly quiet?: boolean;
}): ReactNode {
  return (
    <Surface sectioned data-card="plan-picker">
      <SurfaceHeader title={quiet ? "Come back to a plan" : "Choose a plan"} />
      <SurfaceBody>
        <div className="pp-billing__intervals" role="group" aria-label="Billing interval">
          <Button
            variant={interval === "month" ? "primary" : "secondary"}
            aria-pressed={interval === "month"}
            onClick={() => onInterval("month")}
          >
            Monthly
          </Button>
          <Button
            variant={interval === "year" ? "primary" : "secondary"}
            aria-pressed={interval === "year"}
            onClick={() => onInterval("year")}
          >
            Yearly — two months free
          </Button>
        </div>
        <ul className="pp-billing__tiers">
          {CHECKOUT_TIERS.map((tier) => (
            <li key={tier} className="pp-billing__tier" data-tier={tier}>
              <h3 className="pp-billing__tier-name">{tierName(tier)}</h3>
              <p className="pp-billing__tier-facts">
                {PLANS[tier].activeGtins.toLocaleString("en-US")} active GTINs ·{" "}
                {(PLANS[tier].fairUsePerMonth ?? 0).toLocaleString("en-US")} scans a
                month, fair use
              </p>
              <p className="pp-settings__note" data-statement>
                {checkoutStatement(tier, interval)}
              </p>
              <Button
                variant={quiet ? "secondary" : "primary"}
                loading={choosing === tier}
                disabled={choosing !== null}
                onClick={() => onChoose(tier)}
              >
                {choosing === tier ? "Opening checkout…" : `Choose ${tierName(tier)}`}
              </Button>
            </li>
          ))}
        </ul>
        {refusal !== null && (
          <p className="pp-settings__error" role="alert">
            {refusal}
          </p>
        )}
      </SurfaceBody>
    </Surface>
  );
}

/** `$268`, or `$268.33` when the minor unit is not whole — Stripe reports minor units. */
export function money(minor: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
}

/** The switch's statement before leaving: the preview's figure, or no figure at all. */
export function switchStatement(preview: SwitchPreview): string {
  const leaving =
    " Payment happens on our payment provider's page — you'll come straight back here.";
  if (preview === null) {
    return (
      "The change is pro-rated. Our payment provider states the exact amount before you " +
      "confirm." +
      leaving
    );
  }
  const { amountDueToday, creditToday, currency, nextRenewal } = preview;
  const renewal = `${money(nextRenewal.amount, currency)} on ${chargeDate(nextRenewal.at)}`;
  if (amountDueToday > 0) {
    return `You'll pay a pro-rated ${money(amountDueToday, currency)} today and ${renewal}.${leaving}`;
  }
  if (creditToday > 0) {
    return (
      `You'll pay nothing today. The unused part of your current plan, ` +
      `${money(creditToday, currency)}, is credited to your next invoices, and the plan ` +
      `renews at ${renewal}.${leaving}`
    );
  }
  return `You'll pay nothing today and ${renewal}.${leaving}`;
}

/** A lower tier's limit the organisation already exceeds. The move is admitted — Q-P10. */
export function overLimitNote(tier: CheckoutTier, gtinsActive: number): string | null {
  const limit = PLANS[tier].activeGtins;
  if (gtinsActive <= limit) return null;
  return (
    `${tierName(tier)} includes ${limit.toLocaleString("en-US")} active GTINs, and you have ` +
    `${gtinsActive.toLocaleString("en-US")} published. You can still move: nothing is ` +
    "unpublished, and your printed codes keep resolving."
  );
}

/**
 * The switch picker for an organisation whose subscription grants its tier. Exported for the
 * static suite. A preview of `undefined` is still loading, and its button waits for it.
 */
export function SwitchPicker({
  current,
  gtinsActive,
  previews,
  onChoose,
  choosing,
  refusal,
}: {
  readonly current: Tier;
  readonly gtinsActive: number;
  readonly previews: Partial<Record<CheckoutTier, SwitchPreview | undefined>>;
  readonly onChoose: (tier: CheckoutTier) => void;
  readonly choosing: CheckoutTier | null;
  readonly refusal: string | null;
}): ReactNode {
  return (
    <Surface sectioned data-card="switch-picker">
      <SurfaceHeader title="Change plan" />
      <SurfaceBody>
        <ul className="pp-billing__tiers">
          {CHECKOUT_TIERS.map((tier) => {
            const preview = previews[tier];
            const overLimit = overLimitNote(tier, gtinsActive);
            const { monthlyUsd, yearlyUsd } = PLANS[tier];
            return (
              <li
                key={tier}
                className="pp-billing__tier"
                data-tier={tier}
                data-current={tier === current ? "" : undefined}
              >
                <h3 className="pp-billing__tier-name">{tierName(tier)}</h3>
                <p className="pp-billing__tier-facts">
                  {usd(monthlyUsd ?? 0)} a month or {usd(yearlyUsd ?? 0)} a year ·{" "}
                  {PLANS[tier].activeGtins.toLocaleString("en-US")} active GTINs
                </p>
                {tier === current ? (
                  <Surface tone="inert" size="pill">
                    Current plan
                  </Surface>
                ) : (
                  <>
                    <p className="pp-settings__note" data-statement>
                      {preview === undefined
                        ? "Working out what this change costs…"
                        : switchStatement(preview)}
                    </p>
                    {overLimit !== null && (
                      <p className="pp-settings__note" data-over-limit>
                        {overLimit}
                      </p>
                    )}
                    <Button
                      variant="primary"
                      loading={choosing === tier}
                      disabled={choosing !== null || preview === undefined}
                      onClick={() => onChoose(tier)}
                    >
                      {choosing === tier
                        ? "Opening the plan change…"
                        : `Move to ${tierName(tier)}`}
                    </Button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        {refusal !== null && (
          <p className="pp-settings__error" role="alert">
            {refusal}
          </p>
        )}
      </SurfaceBody>
    </Surface>
  );
}

/** A button the state cards share: it opens Stripe's portal, and states a refusal under it. */
interface PortalAction {
  readonly onManage: (intent: ManageIntent) => void;
  readonly managing: ManageIntent | null;
  readonly refusal: string | null;
}

function Refusal({ refusal }: { readonly refusal: string | null }): ReactNode {
  return refusal === null ? null : (
    <p className="pp-settings__error" role="alert">
      {refusal}
    </p>
  );
}

/** What stays true and what pauses while degraded — the mock's state 4 ledger. */
const DEGRADED_LEDGER = {
  held: [
    "Every printed code still resolving",
    "Retailer and regulator requests unchanged",
    "Product information correct",
  ],
  paused: ["Your branding and layouts paused"],
} as const;

/**
 * The billing state's card — the mock's states 3, 4 and 5 — or nothing while active.
 * Exported for the static suite. `warn` at most, through `BillingSurface`'s type.
 */
export function BillingStateCard({
  standing,
  endedOn = null,
  now = new Date(),
  action,
}: {
  readonly standing: BillingStanding;
  /** The cancelled subscription's period end, where the caller may read it. */
  readonly endedOn?: string | null;
  readonly now?: Date;
  readonly action: PortalAction;
}): ReactNode {
  const tone = BILLING_STATE_TONES[standing.billingStatus];
  if (standing.billingStatus === "grace" && standing.graceSince !== null) {
    const failedOn = new Date(standing.graceSince);
    const deadline = dayName(graceEndsAt(failedOn));
    return (
      <BillingSurface sectioned tone={tone} data-card="billing-state" data-state="grace">
        <SurfaceHeader title={`We couldn't take the payment on ${dayName(failedOn)}`} />
        <SurfaceBody>
          <p className="pp-settings__note">
            Nothing has changed for anyone scanning your products, and nothing will before{" "}
            {deadline}. {paymentFailureSentence(standing.paymentFailure)}
          </p>
          <p className="pp-settings__note" data-consequence>
            If it isn&apos;t sorted by {deadline}, scans start landing on our version of
            your pages instead of yours. Codes keep working, branding goes. One payment
            undoes it.
          </p>
          <Button
            variant="primary"
            loading={action.managing === "payment"}
            disabled={action.managing !== null}
            onClick={() => action.onManage("payment")}
          >
            Update the card
          </Button>
          <Refusal refusal={action.refusal} />
        </SurfaceBody>
      </BillingSurface>
    );
  }
  if (standing.billingStatus === "degraded") {
    const payment = degradedByPayment(standing, now);
    return (
      <BillingSurface
        sectioned
        tone={tone}
        data-card="billing-state"
        data-state="degraded"
      >
        <SurfaceHeader title="Your codes are answering on our pages" />
        <SurfaceBody>
          <ul className="pp-billing__ledger">
            {DEGRADED_LEDGER.held.map((row) => (
              <li key={row} data-ledger="held">
                {row}
              </li>
            ))}
            {DEGRADED_LEDGER.paused.map((row) => (
              <li key={row} data-ledger="paused">
                {row}
              </li>
            ))}
          </ul>
          {payment && (
            <p className="pp-settings__note" data-cause="payment">
              Nothing is deleted and nothing is broken. One payment restores your branding
              within seconds, on the same printed codes.{" "}
              {paymentFailureSentence(standing.paymentFailure)}
            </p>
          )}
          {standing.fairUseOver && (
            <p className="pp-settings__note" data-cause="fair-use">
              {fairUseSentence(standing.plan, now)}
            </p>
          )}
          {payment && (
            <Button
              variant="primary"
              loading={action.managing === "payment"}
              disabled={action.managing !== null}
              onClick={() => action.onManage("payment")}
            >
              Get my branding back
            </Button>
          )}
          <Refusal refusal={action.refusal} />
        </SurfaceBody>
      </BillingSurface>
    );
  }
  if (standing.billingStatus === "churned") {
    return (
      <BillingSurface
        sectioned
        tone={tone}
        data-card="billing-state"
        data-state="churned"
      >
        <SurfaceHeader
          title={
            endedOn === null
              ? "Your plan has ended"
              : `Cancelled on ${chargeDate(endedOn)}`
          }
          meta="No plan"
        />
        <SurfaceBody>
          <p className="pp-settings__note">
            Your codes keep being answered. There is no date on which we stop, and no plan
            you have to be on for that to be true.
          </p>
          <p className="pp-settings__note">
            Your data is still here and still correct. Coming back restores your branding,
            your domain and your layouts within seconds — nothing to re-import, nothing to
            reprint.
          </p>
        </SurfaceBody>
      </BillingSurface>
    );
  }
  return null;
}

/**
 * The reverse trial's card in place of the plan's — the mock's state 1. `warn` in the last
 * three days, the matrix's row. Exported for the static suite.
 */
export function TrialCard({
  standing,
  now = new Date(),
  canLapse,
  lapsing,
  refusal,
  onLapse,
}: {
  readonly standing: TrialStanding;
  readonly now?: Date;
  /** Whether this role may end the trial — the subscription read's answer. */
  readonly canLapse: boolean;
  readonly lapsing: boolean;
  readonly refusal: string | null;
  readonly onLapse: () => void;
}): ReactNode {
  const days = daysLeftOf(standing, now);
  const closing = phaseOf(standing, now) === "closing";
  const ends = standing.trialEndsAt ?? null;
  return (
    <BillingSurface
      sectioned
      tone={closing ? "warn" : "neutral"}
      data-card="trial"
      data-phase={closing ? "closing" : "quiet"}
    >
      <SurfaceHeader
        title="Launch, with Growth switched on"
        meta={`${String(days)} ${days === 1 ? "day" : "days"} left`}
      />
      <SurfaceBody>
        <p className="pp-settings__note">
          You&apos;re not paying anything. For the next {days}{" "}
          {days === 1 ? "day" : "days"} you also have everything Growth includes, so you
          can find out whether you need it.
        </p>
        <ul className="pp-billing__trial-rows">
          {trialUsageRows(standing).map((row) => (
            <li
              key={row.label}
              data-trial-row={row.label}
              data-using={row.using === null ? "none" : String(row.using)}
            >
              <span>{row.label}</span>
              <span>
                {row.using === null ? "not yet" : `using · ${String(row.using)}`}
              </span>
            </li>
          ))}
        </ul>
        <p className="pp-settings__note">
          {closing && ends !== null
            ? `It ends on ${dayName(new Date(ends))}. The dashboard says what changes, with your own numbers.`
            : "We'll tell you what changes three days before it ends, with your own numbers."}
        </p>
        {canLapse && (
          <>
            <p className="pp-settings__note">
              Letting it lapse moves you to Launch now. Your codes keep resolving.
            </p>
            <Button
              variant="secondary"
              loading={lapsing}
              disabled={lapsing}
              onClick={onLapse}
            >
              Let it lapse
            </Button>
            <Refusal refusal={refusal} />
          </>
        )}
      </SurfaceBody>
    </BillingSurface>
  );
}

/** The active state's cancel row — the mock's right rail, Drift Log §U60. Exported for the static suite. */
export function CancelCard({
  plan,
  periodEnd,
  cancelAtPeriodEnd,
  action,
}: {
  readonly plan: Tier;
  readonly periodEnd: string;
  readonly cancelAtPeriodEnd: boolean;
  readonly action: PortalAction;
}): ReactNode {
  if (cancelAtPeriodEnd) {
    return (
      <Surface sectioned data-card="cancel" data-cancelled>
        <SurfaceHeader title={`Your plan ends on ${chargeDate(periodEnd)}`} />
        <SurfaceBody>
          <p className="pp-settings__note">
            Nothing is charged after that. Your codes keep answering, on our pages instead
            of yours.
          </p>
        </SurfaceBody>
      </Surface>
    );
  }
  return (
    <Surface sectioned data-card="cancel">
      <SurfaceHeader title="Cancel" />
      <SurfaceBody>
        <p className="pp-settings__note">
          You can cancel any time and your codes carry on. {tierName(plan)} runs to{" "}
          {chargeDate(periodEnd)}, the end of the period you have paid for. After that,
          scans land on our pages instead of yours.
        </p>
        <Button
          variant="secondary"
          loading={action.managing === "cancel"}
          disabled={action.managing !== null}
          onClick={() => action.onManage("cancel")}
        >
          Cancel {tierName(plan)}
        </Button>
        <Refusal refusal={action.refusal} />
      </SurfaceBody>
    </Surface>
  );
}

/** The opt-in hard cap on active GTINs. Off by default. Exported for the static suite. */
export function HardCapCard({
  hardCap,
  allowance,
  saving,
  refusal,
  onChange,
}: {
  readonly hardCap: boolean;
  readonly allowance: number;
  readonly saving: boolean;
  readonly refusal: string | null;
  readonly onChange: (next: boolean) => void;
}): ReactNode {
  const limit = allowance.toLocaleString("en-US");
  return (
    <Surface sectioned data-card="hard-cap" data-hard-cap={hardCap ? "on" : "off"}>
      <SurfaceHeader title="Stop publishing at your plan's limit" />
      <SurfaceBody>
        <p className="pp-settings__note">
          {hardCap
            ? `On. Once ${limit} products are published, the next publish is refused until ` +
              "you archive one or change plan."
            : `Off. Past ${limit} active GTINs, publishing carries on.`}{" "}
          A recall notice is never refused by the cap.
        </p>
        <Button
          variant="secondary"
          loading={saving}
          disabled={saving}
          onClick={() => onChange(!hardCap)}
        >
          {hardCap ? "Turn the cap off" : "Turn the cap on"}
        </Button>
        <Refusal refusal={refusal} />
      </SurfaceBody>
    </Surface>
  );
}

/**
 * Where Stripe sends invoices — Drift Log §U60's owed observation. Exported for the static
 * suite.
 *
 * `unreadable` states that the provider did not answer instead of showing an address: the
 * value is read through to Stripe per render and nothing stores it here, so the alternative
 * is a figure nobody can date.
 */
export function BillingContactCard({
  email,
  unreadable,
  saving,
  refusal,
  onSave,
}: {
  readonly email: string | null;
  readonly unreadable: boolean;
  readonly saving: boolean;
  readonly refusal: string | null;
  readonly onSave: (email: string) => void;
}): ReactNode {
  const [draft, setDraft] = useState(email ?? "");
  return (
    <Surface sectioned data-card="billing-contact">
      <SurfaceHeader title="Billing contact" />
      <SurfaceBody>
        <p className="pp-settings__note">
          {unreadable
            ? "We could not read this from our payment provider just now, so nothing is " +
              "shown rather than an address that may be out of date. Reload to try again."
            : email === null
              ? "No address is on file, so an invoice has nowhere to go. Add one."
              : `Invoices and payment problems go to ${email}.`}
        </p>
        <form
          className="pp-settings__form"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(draft.trim());
          }}
        >
          <label className="pp-settings__label" htmlFor="pp-billing-contact">
            Where invoices and payment problems go
          </label>
          <input
            id="pp-billing-contact"
            className="pp-settings__input"
            name="billingContact"
            type="email"
            autoComplete="email"
            value={draft}
            placeholder="accounts@your-company.com"
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <Button variant="secondary" type="submit" loading={saving} disabled={saving}>
            Save
          </Button>
          <Refusal refusal={refusal} />
        </form>
      </SurfaceBody>
    </Surface>
  );
}

/** Which picker the screen renders, from the `subscriptions` row and never from `plan`. */
export type BillingDoor = "checkout" | "switch" | "unreadable" | "owed" | "quoted";

export function billingDoor(
  plan: Tier,
  view: SubscriptionView | "unreadable",
): BillingDoor {
  if (view === "unreadable") return "unreadable";
  const sub = view.subscription;
  if (sub !== null && ENTITLING_SUBSCRIPTION_STATUSES.includes(sub.status))
    return "switch";
  if (plan === "enterprise") return "quoted";
  if (sub === null || ENDED_SUBSCRIPTION_STATUSES.includes(sub.status)) return "checkout";
  return "owed";
}

/** How long the screen polls after Stripe sends the customer back, and how often. */
const RETURN_POLL_MS = 2_000;
const RETURN_POLL_FOR_MS = 120_000;

export function BillingSettings(): ReactNode {
  // `?checkout=complete` and `?switch=<tier>` are Stripe's return URLs (`ops/billing.ts`).
  // Read once: the plan moves when the worker applies the webhook, seconds after.
  const [returned] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const switched = CHECKOUT_TIERS.find((tier) => tier === params.get("switch"));
    return {
      at: Date.now(),
      checkout: params.get("checkout") === "complete",
      switchedTo: switched ?? null,
    };
  });
  const returnedAt =
    returned.checkout || returned.switchedTo !== null ? returned.at : null;
  const polling = (settled: boolean): number | false =>
    returnedAt !== null && !settled && Date.now() - returnedAt < RETURN_POLL_FOR_MS
      ? RETURN_POLL_MS
      : false;
  // Both queries poll: the door reads the subscription and the summary reads the plan, and a
  // checkout return that refetched only the plan left the Checkout picker up — Drift §U58a.
  const subscriptionSettled = (view: SubscriptionView | undefined): boolean => {
    const sub = view?.subscription ?? null;
    if (returned.switchedTo !== null) return sub?.plan === returned.switchedTo;
    return sub !== null && ENTITLING_SUBSCRIPTION_STATUSES.includes(sub.status);
  };
  const [interval, setBillingInterval] = useState<BillingInterval>("month");
  const [choosing, setChoosing] = useState<CheckoutTier | null>(null);

  const subscription = useQuery({
    queryKey: ["subscription", "current"],
    queryFn: () => api.subscription(),
    retry: false,
    refetchInterval: (query) =>
      query.state.status === "error"
        ? false
        : polling(subscriptionSettled(query.state.data)),
  });
  const orgSettled = (plan: Tier | undefined): boolean =>
    subscription.isError
      ? // A role that cannot read the subscription waits on the plan alone.
        returned.switchedTo !== null
        ? plan === returned.switchedTo
        : plan !== "launch"
      : subscriptionSettled(subscription.data) &&
        plan === subscription.data?.subscription?.plan;
  // "Let it lapse" ends the trial, and the trial clock moves the plan on its next sweep.
  const [lapsedAt, setLapsedAt] = useState<number | null>(null);
  const lapsePolling = (plan: Tier | undefined): number | false =>
    lapsedAt !== null && plan !== "launch" && Date.now() - lapsedAt < RETURN_POLL_FOR_MS
      ? RETURN_POLL_MS
      : false;
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
    refetchInterval: (query) =>
      polling(orgSettled(query.state.data?.plan)) || lapsePolling(query.state.data?.plan),
  });
  const lapse = useMutation({
    mutationFn: api.endTrial,
    onSuccess: () => {
      setLapsedAt(Date.now());
      void org.refetch();
    },
  });
  const hardCap = useMutation({
    mutationFn: api.setHardCap,
    onSuccess: () => void org.refetch(),
  });
  // Its own query, so an unreachable Stripe fails this row and leaves the rest of the
  // screen readable — the cost Cameron accepted for storing no local copy.
  const contact = useQuery({
    queryKey: ["subscription", "contact"],
    queryFn: () => api.billingContact(),
    retry: false,
  });
  const saveContact = useMutation({
    mutationFn: api.setBillingContact,
    onSuccess: () => void contact.refetch(),
  });
  const checkout = useMutation({
    mutationFn: api.checkout,
    onSuccess: ({ url }) => window.location.assign(url),
    onSettled: (_data, error) => {
      if (error !== null) setChoosing(null);
    },
  });
  const switchPlan = useMutation({
    mutationFn: api.switchPlan,
    onSuccess: ({ url }) => window.location.assign(url),
    onSettled: (_data, error) => {
      if (error !== null) setChoosing(null);
    },
  });
  const [managing, setManaging] = useState<ManageIntent | null>(null);
  const manage = useMutation({
    mutationFn: api.manageBilling,
    onSuccess: ({ url }) => window.location.assign(url),
    onSettled: (_data, error) => {
      if (error !== null) setManaging(null);
    },
  });
  const portal = {
    onManage: (intent: ManageIntent) => {
      setManaging(intent);
      manage.mutate({ intent });
    },
    managing,
    refusal: manage.isError
      ? (problemOf(manage.error)?.detail ??
        "We could not open our payment provider's page. Nothing changed. Try again.")
      : null,
  };

  const current = subscription.data?.subscription ?? null;
  const nextCharge =
    current !== null && current.status === "active" ? current.periodEnd : null;
  const door =
    org.data === undefined || subscription.isPending
      ? null
      : billingDoor(
          org.data.plan,
          subscription.isError ? "unreadable" : subscription.data,
        );
  const switchTiers =
    door === "switch" && current !== null
      ? CHECKOUT_TIERS.filter((tier) => tier !== current.plan)
      : [];
  const previewQueries = useQueries({
    queries: switchTiers.map((tier) => ({
      queryKey: ["subscription", "preview", current?.plan, tier],
      queryFn: () => api.switchPreview({ tier }),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const previews: Partial<Record<CheckoutTier, SwitchPreview | undefined>> = {};
  switchTiers.forEach((tier, i) => {
    const q = previewQueries[i];
    // A refused or failed preview states the change without a figure.
    previews[tier] =
      q === undefined || q.isPending ? undefined : q.isError ? null : q.data;
  });
  const pendingPlan = returnedAt !== null && !orgSettled(org.data?.plan);
  const lapsePending =
    lapsedAt !== null && org.data !== undefined && org.data.plan !== "launch";

  return (
    <section className="pp-settings" data-screen-label="Settings · Billing & usage">
      <h1 className="pp-settings__title">Billing &amp; usage</h1>
      {org.isPending ? (
        <p className="pp-settings__note">Loading your plan…</p>
      ) : org.isError ? (
        <p className="pp-settings__error" role="alert">
          {problemOf(org.error)?.detail ??
            "We could not load your plan. Reload to try again."}
        </p>
      ) : (
        <div className="pp-billing">
          <div className="pp-billing__main">
            {trialRunning(org.data, new Date()) ? (
              <TrialCard
                standing={org.data}
                canLapse={subscription.isSuccess}
                lapsing={lapse.isPending}
                refusal={
                  lapse.isError
                    ? (problemOf(lapse.error)?.detail ??
                      "We could not end the trial. Nothing changed. Try again.")
                    : null
                }
                onLapse={() => lapse.mutate()}
              />
            ) : lapsePending ? (
              <p className="pp-settings__note" role="status" data-lapse-pending>
                Your trial has ended. Your plan moves to Launch within a minute, and your
                codes keep resolving.
              </p>
            ) : (
              <PlanSummary
                plan={org.data.plan}
                billingStatus={org.data.billingStatus}
                nextCharge={nextCharge}
              />
            )}
            <BillingStateCard
              standing={org.data}
              endedOn={current?.status === "canceled" ? current.periodEnd : null}
              action={portal}
            />
            <UsageCard plan={org.data.plan} />
            <HardCapCard
              hardCap={org.data.hardCap}
              allowance={org.data.limits.activeGtins}
              saving={hardCap.isPending}
              refusal={
                hardCap.isError
                  ? (problemOf(hardCap.error)?.detail ??
                    "We could not change the cap. Nothing changed. Try again.")
                  : null
              }
              onChange={(next) => hardCap.mutate({ hardCap: next })}
            />
            {/* `subscription.isSuccess` first: `billing` read and `billing` update hold the
                same two roles, so a role that cannot read the subscription cannot read or
                write this either, and its 403 must not render as "Stripe did not answer".
                `null` data is an organisation with no Stripe customer — no invoice is sent,
                so there is no address to name. */}
            {subscription.isSuccess &&
              !contact.isPending &&
              (contact.isError || contact.data !== null) && (
                <BillingContactCard
                  email={contact.data?.email ?? null}
                  unreadable={contact.isError}
                  saving={saveContact.isPending}
                  refusal={
                    saveContact.isError
                      ? (problemOf(saveContact.error)?.detail ??
                        "We could not change the billing contact. Nothing changed. Try again.")
                      : null
                  }
                  onSave={(email) => saveContact.mutate({ email })}
                />
              )}
            {pendingPlan && (
              <p className="pp-settings__note" role="status" data-checkout-pending>
                {returned.switchedTo !== null
                  ? "Plan change confirmed."
                  : "Checkout finished."}{" "}
                Your plan changes here as soon as our payment provider confirms it,
                usually within seconds. If it has not changed in a few minutes, reload
                this page.
              </p>
            )}
            {door === "switch" && current !== null && (
              <SwitchPicker
                current={current.plan}
                gtinsActive={subscription.data?.gtinsActive ?? 0}
                previews={previews}
                choosing={choosing}
                refusal={
                  switchPlan.isError
                    ? (problemOf(switchPlan.error)?.detail ??
                      "We could not open the plan change. Nothing was charged. Try again.")
                    : null
                }
                onChoose={(tier) => {
                  setChoosing(tier);
                  switchPlan.mutate({ tier });
                }}
              />
            )}
            {door === "switch" && current !== null && (
              <CancelCard
                plan={current.plan}
                periodEnd={current.periodEnd}
                cancelAtPeriodEnd={current.cancelAtPeriodEnd}
                action={portal}
              />
            )}
            {door === "unreadable" && (
              <p className="pp-settings__note" data-door="unreadable">
                Only the organisation&apos;s owner or billing contact can change the plan.
              </p>
            )}
            {door === "owed" && (
              <p className="pp-settings__note" data-door="owed">
                Your plan can change once the outstanding payment has gone through.
              </p>
            )}
            {door === "checkout" && (
              <PlanPicker
                quiet={org.data.billingStatus === "churned"}
                interval={interval}
                onInterval={setBillingInterval}
                choosing={choosing}
                refusal={
                  checkout.isError
                    ? (problemOf(checkout.error)?.detail ??
                      "We could not open checkout. Nothing was charged. Try again.")
                    : null
                }
                onChoose={(tier) => {
                  setChoosing(tier);
                  checkout.mutate({ tier, interval });
                }}
              />
            )}
          </div>
          <div className="pp-billing__rail">
            <NeverFourOhFourCard />
          </div>
        </div>
      )}
    </section>
  );
}

/** The right rail's card — `designs/Screen · Billing and Plan States.dc.html`. */
export function NeverFourOhFourCard(): ReactNode {
  return (
    <Surface sectioned data-card="never-404">
      <SurfaceHeader title="If you ever stop paying" />
      <SurfaceBody>
        <p className="pp-settings__note">
          Your printed codes keep answering. Forever, on any plan, including cancelled.
          That&apos;s a policy, not a courtesy — a barcode on a bottle can&apos;t be
          recalled, so we don&apos;t get to switch it off.
        </p>
        <p className="pp-settings__note">
          What you&apos;d lose is your branding and your domain, not your codes.
        </p>
      </SurfaceBody>
    </Surface>
  );
}
